import axios from 'axios';
import {
  URL_PARAM_COMP_ID,
  URL_PARAM_PAYMENT_TYPE,
  URL_PARAM_STATE,
  URL_PARAM_VIDEO_ID,
  URL_STATE_COMPLETE_PURCHASE,
} from '@wix/wix-vod-shared/dist/src/common/constants/url';
import { hosts } from '@wix/wix-vod-shared/dist/src/common/settings/settings';

const { paypal: DGS_HOST } = hosts;

export const client = axios.create({
  baseURL: `https:${DGS_HOST}/vod`,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true,
  xsrfCookieName: null,
  xsrfHeaderName: null,
});

const APIUrls = {
  config: '/pp/permissions/info',
  order: '/cashier/order',
  status: (order, transaction) =>
    `/cashier/order/${order}/transaction/${transaction}/status`,
  status3ds: transaction => `/cashier/transaction/${transaction}/status`,
};

/**
 * Get payment configuration
 * @param {string} instance
 * @return {Promise<{cashier: any, payPal: any}>}
 */
export function getConfig(instance) {
  return client
    .get(APIUrls.config, { params: { instance } })
    .then(({ data }) => data.paymentMethods);
}

/**
 * Create order object
 * @param {object} params
 * @param {string} params.instance
 * @param {string} params.paymentType
 * @param {string} params.channelId
 * @param {?string} params.videoId
 * @param {string} params.siteUrl
 * @param {string} params.compId
 * @returns {Promise}
 */
export function createOrder({
  instance,
  paymentType,
  channelId,
  videoId,
  siteUrl,
  compId,
}) {
  return client
    .post(
      APIUrls.order,
      {
        channelId,
        refItemId: videoId,
        type: paymentType,
        ref: siteUrl,
        redirectParams: {
          returnUrl: siteUrl,
          [URL_PARAM_COMP_ID]: compId,
          [URL_PARAM_VIDEO_ID]: videoId,
          [URL_PARAM_PAYMENT_TYPE]: paymentType,
          [URL_PARAM_STATE]: URL_STATE_COMPLETE_PURCHASE,
        },
      },
      { params: { instance } },
    )
    .then(({ data }) => ({
      paymentType,
      channelId,
      videoId,
      orderId: data.order.id,
    }));
}

const MAX_RETRIES = 10;
const RETRY_TIMEOUT = 2000;

function wait(milliseconds) {
  return new Promise(resolve => {
    setTimeout(resolve, milliseconds);
  });
}

/**
 * Get payment status from DGS
 * @param {object} params
 * @param {?string} [params.orderId=null]
 * @param {string} params.transactionId
 * @param {string} params.instance
 * @param {?object} [config={}]
 * @param {?number} [config.maxRetries]
 * @param {?number} [config.retryTimeout]
 * @returns {Promise<{status: string}>}
 */
export function getPaymentStatus(
  { orderId = null, transactionId, instance },
  config = {},
) {
  const url = orderId
    ? APIUrls.status(orderId, transactionId)
    : APIUrls.status3ds(transactionId);

  const { maxRetries = MAX_RETRIES, retryTimeout = RETRY_TIMEOUT } = config;
  let requestsCount = 1;

  const getStatus = () =>
    client
      .get(url, { params: { instance } })
      .then(
        response => ({ status: response.data.status }),
        error => ({ status: 'ERROR', error }),
      )
      .then(response => {
        if (response.status === 'CREATED' && requestsCount < maxRetries) {
          requestsCount += 1;
          return wait(retryTimeout).then(getStatus);
        }
        return response;
      });

  return getStatus();
}
